import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import "./AddressesTab.css";
import apiService from "../../services/api_services.js";
import auth_service from "../../../src/services/auth_service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen,faEye, faTrash, faPlus, faHome, faMapMarkerAlt, faCity, faGlobe, faSpinner } from "@fortawesome/free-solid-svg-icons";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import SkeletonLoading from "../../components/SkeletonLoading";


const AddressesTab = () => {
  const customerId = auth_service.getAccessTokenData()?.id;

  const [addresses, setAddresses] = useState([]);
  const [address, setAddress] = useState({
    customerId,
    addressName: "",
    addressLine1: "",
    addressLine2: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  useEffect(() => {
    if (customerId) {
      fetchData();
    }
  }, [customerId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.fetchAddressById(customerId);
      if (response?.data) setAddresses(response.data);
    } catch (err) {
      setErrorMessage("Failed to load addresses. Please try again.");
      toast.error("Failed to load addresses", {
        duration: 3000,
        position: "top-center",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateAddress = () => {
    const newErrors = {};
    const { addressName, addressLine1, pincode, city, state, country } = address;

    if (!addressName) newErrors.addressName = "Address name is required.";
    if (!addressLine1) newErrors.addressLine1 = "Address line 1 is required.";
    if (!pincode) newErrors.pincode = "Pincode is required.";
    else if (!/^\d{5,6}$/.test(pincode)) newErrors.pincode = "Pincode must be 5-6 digits.";
    if (!city) newErrors.city = "City is required.";
    if (!state) newErrors.state = "State is required.";
    if (!country) newErrors.country = "Country is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!validateAddress()) return;
    
    setIsSaving(true);
    try {
      let response;
      const addressToSave = isEditing ? { ...address, id: address.id } : address;

      if (isEditing) {
        response = await apiService.editAddress(addressToSave);
        if (response?.success) {
          toast.success("Address updated successfully", {
            duration: 3000,
            position: "top-center",
          });         
        }
      } else {
        response = await apiService.addAddress(addressToSave);
        if (response?.success) {
          toast.success("Address added successfully", {
            duration: 3000,
            position: "top-center",
          });
        }
      }

      setAddress({
        customerId,
        addressName: "",
        addressLine1: "",
        addressLine2: "",
        pincode: "",
        city: "",
        state: "",
        country: "",
        id: "",
      });
      closeModal();
      fetchData();
    } catch (err) {
      toast.error("Failed to save address. Please try again", {
        duration: 3000,
        position: "top-center",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddress({ ...address, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleDeleteAddress = async (id) => {
    setIsDeleting(id);
    try {
      const response = await apiService.deleteAddress(id);
      if (response?.success) {
        setAddresses(addresses.filter((addr) => addr.id !== id));
        toast.success("Address deleted successfully", {
          duration: 3000,
          position: "top-center",
        });
      } else {
        toast.error("Failed to delete address", {
          duration: 3000,
          position: "top-center",
        });
      }
    } catch (err) {
      toast.error("Failed to delete address", {
        duration: 3000,
        position: "top-center",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const openModal = async (addressToEdit = null) => {
    if (addressToEdit) {
      setIsEditing(true);
      setIsSaving(true); // Show loading state while fetching address details
      try {
        const response = await apiService.fetchLAddressId(addressToEdit.id);
        if (response?.success && response.data?.length > 0) {
          const addressData = response.data[0];
          setAddress({
            customerId,
            addressName: addressData.address_name,
            addressLine1: addressData.address_line_1,
            addressLine2: addressData.address_line_2,
            pincode: addressData.pincode,
            city: addressData.city,
            state: addressData.state,
            country: addressData.country,
            id: addressData.id,
          });
        } else {
          toast.error("Failed to load address details", {
            duration: 3000,
            position: "top-center",
          });
        }
      } catch (err) {
        toast.error("Failed to load address details", {
          duration: 3000,
          position: "top-center",
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      setIsEditing(false);
      setAddress({
        customerId,
        addressName: "",
        addressLine1: "",
        addressLine2: "",
        pincode: "",
        city: "",
        state: "",
        country: "",
        id: "",
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const openViewModal = (address) => {
    setSelectedAddress(address);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedAddress(null);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0 }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
    exit: { y: -20, opacity: 0, transition: { duration: 0.2 } }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-amber-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-900 to-amber-600 bg-clip-text text-transparent">
                  Saved Addresses
                </h1>
                <p className="text-gray-600 mt-2">Manage your delivery addresses</p>
              </div>
              <motion.button 
                onClick={() => openModal()}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200"
                style={{ backgroundColor: '#1F509A' }}
              >
                <FontAwesomeIcon icon={faPlus} className="text-amber-300" />
                <span className="hidden sm:inline">Add New Address</span>
                <span className="sm:hidden">Add Address</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Address List */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6">
            {[1, 2, 3].map((item) => (
              <div key={item} className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden">
                <div className="animate-pulse">
                  {/* Header Skeleton */}
                  <div className="bg-gradient-to-r from-blue-50 to-amber-50 px-6 py-4 border-b border-amber-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                        <div>
                          <div className="h-5 bg-gray-200 rounded w-24 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-16"></div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 bg-gray-200 rounded-full"></div>
                        <div className="w-9 h-9 bg-gray-200 rounded-full"></div>
                        <div className="w-9 h-9 bg-gray-200 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Body Skeleton */}
                  <div className="p-6">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-gray-200 rounded mt-0.5"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-16"></div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-12"></div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-20"></div>
                        </div>
                        <div className="h-6 bg-gray-200 rounded-full w-24"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <AnimatePresence>
            <div className="grid grid-cols-1 gap-6">
              {addresses.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="col-span-full"
                >
                  <div className="bg-white rounded-3xl p-12 text-center shadow-lg border-2 border-dashed border-amber-200">
                    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faHome} className="text-3xl text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-3">No addresses yet</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Add your first delivery address to make ordering faster and easier.
                    </p>
                    <motion.button 
                      onClick={() => openModal()}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200"
                      style={{ backgroundColor: '#1F509A' }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="text-amber-300" />
                      Add Your First Address
                    </motion.button>
                  </div>
                </motion.div>
              ) : (
                addresses.map((addr, index) => (
                  <motion.div
                    key={addr.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl shadow-lg border border-amber-100 hover:shadow-xl transition-all duration-300 overflow-hidden group"
                  >
                    {/* Address Card Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-amber-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-amber-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-600 to-amber-500 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                            <FontAwesomeIcon icon={faHome} className="text-white text-xs sm:text-sm" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-bold text-gray-800 text-base sm:text-lg truncate">
                              {addr.address_name}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 truncate">
                              {[addr.city, addr.state].filter(Boolean).join(', ') || 'Location not specified'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-1.5 sm:gap-2 sm:ml-auto">
                          <motion.button
                            onClick={() => openViewModal(addr)}
                            whileHover={{ scale: 1.1, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            className="w-8 h-8 sm:w-9 sm:h-9 bg-green-100 hover:bg-green-200 text-green-600 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faEye} className="text-xs sm:text-sm" />
                          </motion.button>

                          <motion.button
                            onClick={() => openModal(addr)}
                            whileHover={{ scale: 1.1, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                            title="Edit Address"
                          >
                            <FontAwesomeIcon icon={faPen} className="text-xs sm:text-sm" />
                          </motion.button>

                          <motion.button
                            onClick={() => handleDeleteAddress(addr.id)}
                            whileHover={{ scale: 1.1, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            disabled={isDeleting === addr.id}
                            className={`w-8 h-8 sm:w-9 sm:h-9 bg-red-100 hover:bg-red-200 text-red-600 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md ${
                              isDeleting === addr.id ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title="Delete Address"
                          >
                            {isDeleting === addr.id ? (
                              <FontAwesomeIcon icon={faSpinner} spin className="text-xs sm:text-sm" />
                            ) : (
                              <FontAwesomeIcon icon={faTrash} className="text-xs sm:text-sm" />
                            )}
                          </motion.button>
                        </div>
                      </div>
                    </div>

                    {/* Address Card Body */}
                                     </motion.div>
                ))
              )}
            </div>
          </AnimatePresence>
        )}

        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          className="w-full h-full sm:max-w-2xl sm:mx-auto sm:h-auto bg-white sm:rounded-3xl shadow-2xl border border-amber-100 overflow-hidden flex flex-col"
          overlayClassName="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4"
          style={{
            overlay: {
              zIndex: 1000,
            }
          }}
        >
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 sm:p-8 pt-8 sm:pt-6"
               style={{ background: 'linear-gradient(to right, #1F509A, #000066)', paddingTop: 'max(2rem, env(safe-area-inset-top))' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={isEditing ? faPen : faPlus} className="text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  {isEditing ? "Edit Address" : "Add New Address"}
                </h2>
              </div>
              <button 
                onClick={closeModal}
                className="text-white hover:text-amber-300 transition-colors p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Modal Body */}
          <div className="flex-1 p-4 sm:p-8 overflow-y-auto pb-4 sm:pb-8 max-h-[calc(100vh-200px)] sm:max-h-[70vh]">
            <form onSubmit={handleSaveAddress} className="space-y-6">
              {/* Address Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Address Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
              
                  <input
                    type="text"
                    name="addressName"
                    value={address.addressName}
                    onChange={handleChange}
                    placeholder="Home, Work, etc."
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                      errors.addressName 
                        ? 'border-red-300 focus:border-red-500 bg-red-50' 
                        : 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                    }`}
                    disabled={isSaving}
                  />
                </div>
                {errors.addressName && (
                  <motion.p 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-500 text-sm mt-1 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.addressName}
                  </motion.p>
                )}
              </div>
              
              {/* Address Line 1 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Address Line 1 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
               
                  <input
                    type="text"
                    name="addressLine1"
                    value={address.addressLine1}
                    onChange={handleChange}
                    placeholder="Street address, P.O. box, company name"
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                      errors.addressLine1 
                        ? 'border-red-300 focus:border-red-500 bg-red-50' 
                        : 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                    }`}
                    disabled={isSaving}
                  />
                </div>
                {errors.addressLine1 && (
                  <motion.p 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-500 text-sm mt-1 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.addressLine1}
                  </motion.p>
                )}
              </div>
              
              {/* Address Line 2 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Address Line 2 <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="addressLine2"
                  value={address.addressLine2}
                  onChange={handleChange}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 bg-gray-50 focus:bg-white transition-all duration-200"
                  disabled={isSaving}
                />
              </div>
              
              {/* Pincode and City */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    value={address.pincode}
                    onChange={handleChange}
                    placeholder="Postal code"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                      errors.pincode 
                        ? 'border-red-300 focus:border-red-500 bg-red-50' 
                        : 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                    }`}
                    disabled={isSaving}
                  />
                  {errors.pincode && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.pincode}
                    </motion.p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                 
                    <input
                      type="text"
                      name="city"
                      value={address.city}
                      onChange={handleChange}
                      placeholder="City"
                      className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                        errors.city 
                          ? 'border-red-300 focus:border-red-500 bg-red-50' 
                          : 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                      }`}
                      disabled={isSaving}
                    />
                  </div>
                  {errors.city && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.city}
                    </motion.p>
                  )}
                </div>
              </div>
              
              {/* State and Country */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={address.state}
                    onChange={handleChange}
                    placeholder="State/Province/Region"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                      errors.state 
                        ? 'border-red-300 focus:border-red-500 bg-red-50' 
                        : 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                    }`}
                    disabled={isSaving}
                  />
                  {errors.state && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.state}
                    </motion.p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                  
                    <input
                      type="text"
                      name="country"
                      value={address.country}
                      onChange={handleChange}
                      placeholder="Country"
                      className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                        errors.country 
                          ? 'border-red-300 focus:border-red-500 bg-red-50' 
                          : 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                      }`}
                      disabled={isSaving}
                    />
                  </div>
                  {errors.country && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.country}
                    </motion.p>
                  )}
                </div>
              </div>
              
            </form>
          </div>
          
          {/* Form Buttons - Sticky at bottom on mobile */}
          <div className="sticky bottom-0 sm:static bg-white border-t sm:border-t-0 p-4 sm:p-6" 
               style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                type="button" 
                onClick={closeModal}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
                disabled={isSaving}
              >
                Cancel
              </button>
              
              <motion.button 
                type="submit" 
                onClick={handleSaveAddress}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSaving}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50"
                style={{ backgroundColor: '#1F509A' }}
              >
                {isSaving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>{isEditing ? "Saving..." : "Adding..."}</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={isEditing ? faPen : faPlus} className="text-amber-300" />
                    <span>{isEditing ? "Save Changes" : "Save Address"}</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </Modal>

        {/* View Address Modal */}
        <Modal
          isOpen={isViewModalOpen}
          onRequestClose={closeViewModal}
          className="w-full h-full sm:max-w-2xl sm:mx-auto sm:h-auto bg-white sm:rounded-3xl shadow-2xl border border-amber-100 overflow-hidden flex flex-col"
          overlayClassName="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4"
          style={{
            overlay: {
              zIndex: 1000,
            }
          }}
        >
          {selectedAddress && (
            <>
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 sm:p-8 pt-8 sm:pt-6"
                   style={{ background: 'linear-gradient(to right, #1F509A, #000066)', paddingTop: 'max(2rem, env(safe-area-inset-top))' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-white" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white">
                      Address Details
                    </h2>
                  </div>
                  <button 
                    onClick={closeViewModal}
                    className="text-white hover:text-amber-300 transition-colors p-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Modal Body */}
              <div className="flex-1 p-4 sm:p-8 overflow-y-auto pb-4 sm:pb-8 max-h-[calc(100vh-200px)] sm:max-h-[70vh]">
                <div className="space-y-6">
                  
                  {/* Address Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address Name
                    </label>
                    <div className="relative">
                      <FontAwesomeIcon icon={faHome} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <div className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">
                        {selectedAddress.address_name}
                      </div>
                    </div>
                  </div>
                  
                  {/* Address Line 1 */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address Line 1
                    </label>
                    <div className="relative">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <div className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-800">
                        {selectedAddress.address_line_1 || 'Not specified'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Address Line 2 */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address Line 2 <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-800">
                      {selectedAddress.address_line_2 && selectedAddress.address_line_2.trim() 
                        ? selectedAddress.address_line_2 
                        : 'Not specified'
                      }
                    </div>
                  </div>
                  
                  {/* Pincode and City */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Pincode
                      </label>
                      <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">
                        {selectedAddress.pincode || 'Not specified'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        City
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon icon={faCity} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <div className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-800">
                          {selectedAddress.city || 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* State and Country */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        State
                      </label>
                      <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-800">
                        {selectedAddress.state || 'Not specified'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Country
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon icon={faGlobe} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <div className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-800">
                          {selectedAddress.country || 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>

                
                </div>
              </div>
              
              {/* Action Buttons - Sticky at bottom on mobile */}
              <div className="sticky bottom-0 sm:static bg-white border-t sm:border-t-0 p-4 sm:p-6"
                   style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.button 
                    onClick={closeViewModal}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200"
                  >
                    Close
                  </motion.button>
                  
                  <motion.button 
                    onClick={() => {
                      closeViewModal();
                      openModal(selectedAddress);
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-xl transition-all duration-200"
                    style={{ backgroundColor: '#1F509A' }}
                  >
                    <FontAwesomeIcon icon={faPen} className="text-amber-300" />
                    <span>Edit Address</span>
                  </motion.button>
                </div>
              </div>
            </>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default AddressesTab;
